import { Link } from 'react-router-dom';
import type { ElementType } from 'react';
import { Box, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

export interface AdminNavRowProps {
  to: string;
  icon: ElementType;
  title: string;
  desc: string;
  /** Optional trailing count (e.g. total users). Hidden while null/undefined. */
  count?: number | null;
  /** Attention badge (e.g. items needing review). Hidden unless > 0. */
  badgeCount?: number | null;
}

/**
 * A single scannable navigation row for admin hub pages
 * (System Administration, Tenant Administration).
 */
export default function AdminNavRow({ to, icon: Icon, title, desc, count, badgeCount }: AdminNavRowProps) {
  return (
    <Box
      component={Link}
      to={to}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        textDecoration: 'none',
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          bgcolor: 'surface.raised',
          boxShadow: 1,
          '& .nav-chevron': { opacity: 1, transform: 'translateX(0)' },
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      <Icon sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.4 }}>
          {title}
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.4 }}>
          {desc}
        </Typography>
      </Box>
      {badgeCount != null && badgeCount > 0 && (
        <Box
          sx={{
            px: 1,
            py: 0.25,
            borderRadius: 10,
            bgcolor: 'status.attention.bg',
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'status.attention.fg', fontVariantNumeric: 'tabular-nums', lineHeight: 1.4 }}
          >
            {badgeCount}
          </Typography>
        </Box>
      )}
      {count != null && (
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: 'text.secondary', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}
        >
          {count}
        </Typography>
      )}
      <ChevronRightIcon
        className="nav-chevron"
        sx={{
          fontSize: 18,
          color: 'primary.main',
          flexShrink: 0,
          opacity: 0,
          transform: 'translateX(-4px)',
          transition: 'opacity 0.15s ease, transform 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />
    </Box>
  );
}
